import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import {
    calculateMobilityLinePricingTotal,
    formatMobilityLinePricingNote,
    mobilityLinePricingModes,
    normalizeMobilityLinePricingForEditor
} from '../fields/mobilityLinePricing';

function CatalogEditorDialog({ context, primitives }) {
    const {
        addCatalogDisplayChip,
        addCatalogLinePricingTier,
        addCatalogTopBadge,
        config,
        deleteDraftOnlyProduct,
        draftConfig,
        editingCatalog,
        findActiveCatalogProduct,
        formatMoney,
        isDraftOnlyProduct,
        isValidSortOrderInput,
        removeCatalogDisplayChip,
        removeCatalogLinePricingTier,
        removeCatalogTopBadge,
        saveCatalogDraft,
        savingCatalogDraft,
        scopeLabel,
        setEditingCatalog,
        updateCatalogDisplayChip,
        updateCatalogEditorValue,
        updateCatalogLinePricing,
        updateCatalogLinePricingTier,
        updateCatalogTopBadge,
        validateAddonsEditor,
        validateMobilityEditor,
        validateSecurityEditor
    } = context;
    const {
        renderChipListEditor,
        renderOfferLifecycleControls,
        renderProductDraftPreview
    } = primitives;

    const renderCatalogEditDialog = () => {
        if (!editingCatalog) {
            return null;
        }

        const values = editingCatalog.values;
        const localErrors = editingCatalog.scope === 'mobility'
            ? validateMobilityEditor(values)
            : editingCatalog.scope === 'security'
                ? validateSecurityEditor(values)
                : validateAddonsEditor(values);
        const sortOrderInvalid = !isValidSortOrderInput(values.sortOrder);
        const scope = editingCatalog.scope;
        const mobilityCategories = draftConfig?.products?.mobility?.categories || config?.products?.mobility?.categories || [];
        const activeProduct = findActiveCatalogProduct(scope, editingCatalog.originalId, editingCatalog.groupId);
        const canDelete = isDraftOnlyProduct({ id: editingCatalog.originalId, ...values }, activeProduct);
        const mobilityLinePricing = scope === 'mobility'
            ? values.linePricing || normalizeMobilityLinePricingForEditor({ id: editingCatalog.originalId, price: values.price })
            : null;
        const mobilityLinePricingPreview = scope === 'mobility'
            ? [1, 2, 3, 4, 5]
                .filter((lineCount) => !mobilityLinePricing?.maxLines || Number(mobilityLinePricing.maxLines) >= lineCount)
                .map((lineCount) => ({
                    lineCount,
                    total: calculateMobilityLinePricingTotal(mobilityLinePricing, lineCount, values.price)
                }))
            : [];

        return (
            <Dialog open onClose={() => setEditingCatalog(null)} fullWidth maxWidth="md">
                <DialogTitle>{scopeLabel(scope)} Draft Edit</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning" variant="outlined">
                            Saving updates the draft config only. Publish {scopeLabel(scope)} Draft is required before Bundle Builder sees these values.
                        </Alert>
                        <Alert severity="info" variant="outlined">
                            Most updates only need Offer Settings, Visibility, and Expiry Date. Customer email output changes live in the Customer Email Settings section.
                        </Alert>
                        <Typography variant="h6">Offer Settings / Bundle Builder Display</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={scope === 'addons' ? 6 : 12}>
                                <TextField
                                    label="Product key"
                                    value={editingCatalog.originalId}
                                    fullWidth
                                    disabled
                                    helperText="Product keys cannot change in this phase."
                                />
                            </Grid>
                            {scope === 'addons' && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Add-on group"
                                        value={editingCatalog.groupId || ''}
                                        fullWidth
                                        disabled
                                        helperText="Add-on group movement is not enabled in this phase."
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    label="Display name"
                                    value={values.displayName}
                                    onChange={(event) => updateCatalogEditorValue('displayName', event.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Sort order"
                                    type="number"
                                    value={values.sortOrder}
                                    onChange={(event) => updateCatalogEditorValue('sortOrder', event.target.value)}
                                    fullWidth
                                    error={sortOrderInvalid}
                                    helperText={sortOrderInvalid ? 'Sort order must be numeric.' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Price"
                                    type="number"
                                    value={values.price}
                                    onChange={(event) => updateCatalogEditorValue('price', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Regular/reference price"
                                    type="number"
                                    value={values.regularPrice}
                                    onChange={(event) => updateCatalogEditorValue('regularPrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {scope === 'mobility' ? (
                                    <TextField
                                        label="Category/provider"
                                        select
                                        value={values.category}
                                        onChange={(event) => updateCatalogEditorValue('category', event.target.value)}
                                        fullWidth
                                        SelectProps={{ native: true }}
                                    >
                                        {mobilityCategories.map((category) => (
                                            <option key={category.id} value={category.id}>{category.label || category.id}</option>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        label="Category/group"
                                        value={scope === 'addons' ? editingCatalog.groupId || '' : values.category}
                                        onChange={(event) => updateCatalogEditorValue('category', event.target.value)}
                                        fullWidth
                                        disabled={scope === 'addons'}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Provider/type"
                                    value={values.type}
                                    onChange={(event) => updateCatalogEditorValue('type', event.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Term"
                                    value={values.term}
                                    onChange={(event) => updateCatalogEditorValue('term', event.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Device notes"
                                    value={values.devices}
                                    onChange={(event) => updateCatalogEditorValue('devices', event.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label="Internal / card note"
                            value={values.note}
                            onChange={(event) => updateCatalogEditorValue('note', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        {scope === 'mobility' && (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>Line Pricing Rules</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Controls the actual Mobility line total in Bundle Builder. The text note is only display; pricing math comes from these fields after publish.
                                        </Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Pricing mode"
                                                select
                                                value={mobilityLinePricing.mode}
                                                onChange={(event) => updateCatalogLinePricing('mode', event.target.value)}
                                                fullWidth
                                                SelectProps={{ native: true }}
                                            >
                                                {mobilityLinePricingModes.map((mode) => (
                                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Max lines optional"
                                                type="number"
                                                value={mobilityLinePricing.maxLines}
                                                onChange={(event) => updateCatalogLinePricing('maxLines', event.target.value)}
                                                fullWidth
                                                inputProps={{ min: 1, step: 1 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Display note override"
                                                value={mobilityLinePricing.noteTemplate}
                                                onChange={(event) => updateCatalogLinePricing('noteTemplate', event.target.value)}
                                                fullWidth
                                                helperText="Optional"
                                            />
                                        </Grid>
                                    </Grid>

                                    {mobilityLinePricing.mode === 'flatPerLine' && (
                                        <TextField
                                            label="Flat price per line"
                                            type="number"
                                            value={mobilityLinePricing.flatPricePerLine}
                                            onChange={(event) => updateCatalogLinePricing('flatPricePerLine', event.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0, step: '0.01' }}
                                        />
                                    )}

                                    {mobilityLinePricing.mode === 'firstLineAdditional' && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="First line price"
                                                    type="number"
                                                    value={mobilityLinePricing.firstLinePrice}
                                                    onChange={(event) => updateCatalogLinePricing('firstLinePrice', event.target.value)}
                                                    fullWidth
                                                    inputProps={{ min: 0, step: '0.01' }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Additional line price"
                                                    type="number"
                                                    value={mobilityLinePricing.additionalLinePrice}
                                                    onChange={(event) => updateCatalogLinePricing('additionalLinePrice', event.target.value)}
                                                    fullWidth
                                                    inputProps={{ min: 0, step: '0.01' }}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {mobilityLinePricing.mode === 'totalLineCountTier' && (
                                        <Stack spacing={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                                <Typography variant="subtitle2">Line count tiers</Typography>
                                                <Button size="small" variant="outlined" onClick={addCatalogLinePricingTier}>
                                                    Add Tier
                                                </Button>
                                            </Box>
                                            {(mobilityLinePricing.tiers || []).map((tier, index) => (
                                                <Grid container spacing={1} key={`tier-${index}`} alignItems="center">
                                                    <Grid item xs={6} sm={2}>
                                                        <TextField
                                                            label="Min"
                                                            type="number"
                                                            value={tier.minLines}
                                                            onChange={(event) => updateCatalogLinePricingTier(index, 'minLines', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{ min: 1, step: 1 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} sm={2}>
                                                        <TextField
                                                            label="Max"
                                                            type="number"
                                                            value={tier.maxLines}
                                                            onChange={(event) => updateCatalogLinePricingTier(index, 'maxLines', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{ min: 1, step: 1 }}
                                                            helperText="Blank = open"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            label="Price / line"
                                                            type="number"
                                                            value={tier.pricePerLine}
                                                            onChange={(event) => updateCatalogLinePricingTier(index, 'pricePerLine', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{ min: 0, step: '0.01' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            label="Label"
                                                            value={tier.label}
                                                            onChange={(event) => updateCatalogLinePricingTier(index, 'label', event.target.value)}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={2}>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            onClick={() => removeCatalogLinePricingTier(index)}
                                                            disabled={(mobilityLinePricing.tiers || []).length <= 1}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Stack>
                                    )}

                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                                        <Typography variant="subtitle2" gutterBottom>Live pricing preview</Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {formatMobilityLinePricingNote(mobilityLinePricing, values.price)}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {mobilityLinePricingPreview.map((preview) => (
                                                <Grid item xs={6} sm={2.4} key={preview.lineCount}>
                                                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {preview.lineCount} line{preview.lineCount === 1 ? '' : 's'}
                                                        </Typography>
                                                        <Typography variant="subtitle2">{formatMoney(preview.total)}</Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Stack>
                            </Paper>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                            <FormControlLabel control={<Switch checked={values.visible} onChange={(event) => updateCatalogEditorValue('visible', event.target.checked)} />} label="Visible" />
                            <FormControlLabel control={<Switch checked={values.quoteEligible} onChange={(event) => updateCatalogEditorValue('quoteEligible', event.target.checked)} />} label="Quote eligible" />
                            <FormControlLabel control={<Switch checked={values.separateFlowOnly} onChange={(event) => updateCatalogEditorValue('separateFlowOnly', event.target.checked)} />} label="Separate flow only" />
                            <FormControlLabel control={<Switch checked={values.allowQuantity} onChange={(event) => updateCatalogEditorValue('allowQuantity', event.target.checked)} />} label="Quantity allowed" />
                            <FormControlLabel control={<Switch checked={values.recommended} onChange={(event) => updateCatalogEditorValue('recommended', event.target.checked)} />} label="Recommended" />
                            <FormControlLabel control={<Switch checked={values.hot} onChange={(event) => updateCatalogEditorValue('hot', event.target.checked)} />} label="Hot" />
                        </Stack>
                        <TextField
                            label="Highlight note"
                            value={values.highlightNote}
                            onChange={(event) => updateCatalogEditorValue('highlightNote', event.target.value)}
                            fullWidth
                        />
                        {renderOfferLifecycleControls({
                            values,
                            updateValue: updateCatalogEditorValue,
                            canDelete,
                            onDelete: () => deleteDraftOnlyProduct({ scope, productId: editingCatalog.originalId, groupName: editingCatalog.groupId }),
                            helperScope: scopeLabel(scope)
                        })}
                        <Divider />
                        <Box component="details">
                            <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                Advanced Settings
                            </Box>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <TextField
                                    label="Internal agent note"
                                    value={values.internalNotesText}
                                    onChange={(event) => updateCatalogEditorValue('internalNotesText', event.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    helperText="Use one note per line."
                                />
                            </Stack>
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Offer Display Details</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Draft display settings affect Bundle Builder only after {scopeLabel(scope)} publish.
                            </Typography>
                        </Box>
                        <TextField
                            label="Card subtitle / description"
                            value={values.cardDescription}
                            onChange={(event) => updateCatalogEditorValue('cardDescription', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Display highlight note"
                            value={values.displayHighlightNote}
                            onChange={(event) => updateCatalogEditorValue('displayHighlightNote', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Internal agent display note"
                            value={values.internalAgentNote}
                            onChange={(event) => updateCatalogEditorValue('internalAgentNote', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Selected badge text"
                            value={values.selectedBadgeText}
                            onChange={(event) => updateCatalogEditorValue('selectedBadgeText', event.target.value)}
                            fullWidth
                        />
                        {renderChipListEditor({
                            title: 'Top Card Badges',
                            helperText: 'Top Card Badges appear at the top of the Bundle Builder card.',
                            chips: values.topBadges,
                            onUpdate: updateCatalogTopBadge,
                            onAdd: addCatalogTopBadge,
                            onRemove: removeCatalogTopBadge,
                            addLabel: 'Add Badge'
                        })}
                        {renderChipListEditor({
                            title: 'Detail Chips',
                            helperText: 'Detail Chips appear inside the card under the product description.',
                            chips: values.displayChips,
                            onUpdate: updateCatalogDisplayChip,
                            onAdd: addCatalogDisplayChip,
                            onRemove: removeCatalogDisplayChip,
                            addLabel: 'Add Chip'
                        })}
                        <Box>
                            {renderProductDraftPreview(values, {
                                categoryLabel: scopeLabel(scope),
                                priceValue: values.price
                            })}
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Customer Email Settings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                These fields control customer-facing email output only.
                            </Typography>
                        </Box>
                        <FormControlLabel control={<Switch checked={values.customerEmailEligible} onChange={(event) => updateCatalogEditorValue('customerEmailEligible', event.target.checked)} />} label="Customer email eligible" />
                        <TextField
                            label="Customer-facing email name"
                            value={values.emailDisplayName}
                            onChange={(event) => updateCatalogEditorValue('emailDisplayName', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Customer-facing email bullets"
                            value={values.emailBulletsText}
                            onChange={(event) => updateCatalogEditorValue('emailBulletsText', event.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Use one bullet per line. Required when customer email eligible is on."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingCatalog(null)} disabled={savingCatalogDraft}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveCatalogDraft} disabled={savingCatalogDraft || localErrors.length > 0 || sortOrderInvalid}>
                        {savingCatalogDraft ? 'Saving...' : 'Save Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    return renderCatalogEditDialog();
}

export default CatalogEditorDialog;
